import {
  AccumulatedConsumption,
  ComparativeConsumptionByInstallation,
  ConsumptionFilter,
} from '@core/domain/model/consumption/consumption.model';
import {
  AccumulatedConsumptionApi,
  ComparativeConsumptionApi,
  ConsumptionFilterApi,
  DailyAccumulatedConsumptionApi,
  DailyComparativeConsumptionApi,
  MonthlyAccumulatedConsumptionApi,
  MonthlyComparativeConsumptionApi,
  YearlyAccumulatedConsumptionApi,
  YearlyComparativeConsumptionApi,
} from '@core/api/model/consumption/consumption-api.model';
import { DateUtil } from '@core/services/date/date.util';
import { parse } from 'date-fns';
import { MasterInstallationModel } from '@core/domain/model/master/master-installation.model';
import { groupBy } from 'lodash-es';

export class ConsumptionMapper {
  public static filterToApi(domain: ConsumptionFilter): ConsumptionFilterApi {
    return {
      ListaCups: domain.installations.map((value) => value.cups),
      FechaDesde: DateUtil.toBackString(domain.startDate)!,
      FechaHasta: DateUtil.toBackString(domain.endDate)!,
    };
  }
  public static accumulatedDailyToDomain(api: AccumulatedConsumptionApi<DailyAccumulatedConsumptionApi>): Array<AccumulatedConsumption> {
    const consumptionList = api
      .flatMap((value) => value.consumos)
      .flatMap((dayConsumption) =>
        dayConsumption.datosConsumos.map((hoursConsumptions) => ({
          dateString: `${dayConsumption.dia}___${hoursConsumptions.hora}`,
          value: hoursConsumptions.consumo,
        }))
      );
    const grouped = groupBy(consumptionList, (value) => value.dateString);
    return Object.entries(grouped).map(([dateString, value]) => {
      const dateStringSplit = dateString.split('___');
      const date = `${dateStringSplit[0]}T00:00:00`;
      const hour = dateStringSplit[1];
      return {
        date: parse(date, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
        hour,
        value: value.reduce((acc, currentValue) => acc + currentValue.value, 0),
      };
    });
  }

  public static accumulatedMonthlyToDomain(
    api: AccumulatedConsumptionApi<MonthlyAccumulatedConsumptionApi>
  ): Array<AccumulatedConsumption> {
    const consumptionList = api
      .flatMap((value) => value.consumos)
      .flatMap((monthConsumption) =>
        monthConsumption.datosConsumos.map((daysConsumptions) => ({
          dateString: `${daysConsumptions.dia}T00:00:00`,
          value: daysConsumptions.consumo,
        }))
      );
    return ConsumptionMapper.accumulatedMapToDomain(consumptionList);
  }

  public static accumulatedYearlyToDomain(api: AccumulatedConsumptionApi<YearlyAccumulatedConsumptionApi>): Array<AccumulatedConsumption> {
    const consumptionList = api
      .flatMap((value) => value.consumos)
      .flatMap((yearConsumptions) => yearConsumptions.datosConsumos)
      .map((monthsConsumptions) => ({
        dateString: `${monthsConsumptions.anio}-${monthsConsumptions.mes}-01T00:00:00`,
        value: monthsConsumptions.consumo,
      }));
    return ConsumptionMapper.accumulatedMapToDomain(consumptionList);
  }

  private static accumulatedMapToDomain(consumptionList: Array<{ dateString: string; value: number }>): Array<AccumulatedConsumption> {
    const consumptionMap = consumptionList.reduce(
      (acc, currentValue) => {
        const key = currentValue.dateString;
        let value = currentValue.value;
        if (acc[key] != null) {
          value += acc[key];
        }
        return {
          ...acc,
          [key]: value,
        };
      },
      {} as Record<string, number>
    );
    return Object.entries(consumptionMap).map(([dateString, value]) => ({
      date: parse(dateString, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
      value,
    }));
  }

  public static comparativeDailyToDomain(
    api: ComparativeConsumptionApi<DailyComparativeConsumptionApi>,
    masterInstallations: MasterInstallationModel[]
  ): Array<ComparativeConsumptionByInstallation> {
    return api.map((consumptionByCups) => {
      const installationCups = consumptionByCups.cups;
      const installationName = masterInstallations.find((value) => value.cups === installationCups)?.nombre ?? installationCups;
      return {
        installationName,
        installationCups,
        consumption: consumptionByCups.datosConsumos.map((hoursConsumptions) => {
          return {
            value: hoursConsumptions.consumo,
            hour: hoursConsumptions.hora,
            date: parse(`${hoursConsumptions.dia}T00:00:00`, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
          };
        }),
      };
    });
  }

  public static comparativeMonthlyToDomain(
    api: ComparativeConsumptionApi<MonthlyComparativeConsumptionApi>,
    masterInstallations: MasterInstallationModel[]
  ): Array<ComparativeConsumptionByInstallation> {
    return api.map((consumptionByCups) => {
      const installationCups = consumptionByCups.cups;
      const installationName = masterInstallations.find((value) => value.cups === installationCups)?.nombre ?? installationCups;
      return {
        installationName,
        installationCups,
        consumption: consumptionByCups.datosConsumos.map((daysConsumptions) => {
          return {
            value: daysConsumptions.consumo,
            date: parse(`${daysConsumptions.dia}T00:00:00`, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
          };
        }),
      };
    });
  }

  public static comparativeYearlyToDomain(
    api: ComparativeConsumptionApi<YearlyComparativeConsumptionApi>,
    masterInstallations: MasterInstallationModel[]
  ): Array<ComparativeConsumptionByInstallation> {
    return api.map((consumptionByCups) => {
      const installationCups = consumptionByCups.cups;
      const installationName = masterInstallations.find((value) => value.cups === installationCups)?.nombre ?? installationCups;
      return {
        installationName,
        installationCups,
        consumption: consumptionByCups.datosConsumos.map((monthsConsumptions) => {
          return {
            value: monthsConsumptions.consumo,
            date: parse(`${monthsConsumptions.anio}-${monthsConsumptions.mes}-01T00:00:00`, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
          };
        }),
      };
    });
  }
}
